import * as XLSX from 'xlsx';

const DELIMITERS = [';', ',', '\t'];

// The delimiter used most in the first non-empty line (Excel in Sweden and
// Norway writes CSV with ";")
const detectDelimiter = (text) => {
  const firstLine = text.split(/\r?\n/).find((line) => line.trim()) || '';
  const [best] = DELIMITERS.map((delimiter) => ({
    delimiter,
    count: firstLine.split(delimiter).length - 1,
  })).sort((a, b) => b.count - a.count);
  return best.count > 0 ? best.delimiter : ',';
};

const pad = (number) => String(number).padStart(2, '0');

// Excel date cells come back as local midnight, at times a few minutes off:
// rounded to the nearest day and written as YYYY-MM-DD
const toDay = (date) => {
  const rounded = new Date(date.getTime() + 12 * 60 * 60 * 1000);
  return `${rounded.getFullYear()}-${pad(rounded.getMonth() + 1)}-${pad(
    rounded.getDate()
  )}`;
};

const cellText = (value) =>
  value instanceof Date ? toDay(value) : `${value ?? ''}`.trim();

/**
 * Reads the first sheet of a CSV or Excel file as text: { headers, rows }.
 * CSV values stay as written (a phone number keeps its leading 0); Excel
 * dates come back as YYYY-MM-DD. Empty rows are left out.
 */
const readSpreadsheet = async (file) => {
  const isCsv = /\.(csv|txt)$/i.test(file.name);
  let workbook;
  if (isCsv) {
    const text = (await file.text()).replace(/^﻿/, '');
    workbook = XLSX.read(text, {
      type: 'string',
      FS: detectDelimiter(text),
      raw: true,
    });
  } else {
    workbook = XLSX.read(await file.arrayBuffer(), {
      type: 'array',
      cellDates: true,
    });
  }
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  // Raw values: a cell's own display format (e.g. US dates) is ignored
  const table = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: false,
  });
  const [headerRow = [], ...rows] = table;
  const headers = headerRow.map(cellText);
  return {
    headers,
    rows: rows
      .map((row) => headers.map((_, index) => cellText(row[index])))
      .filter((row) => row.some(Boolean)),
  };
};

export default readSpreadsheet;
